// Author: Sunil Jain
// Timestamp: 2025-04-23T12:49:46EDT // Corrected syntax error
// File: src/pages/OnboardingTraining.js
// Description: Displays mandatory training content and allows users to mark it as complete.

import React, { useState, useEffect } from 'react'; // Added useEffect for potential redirect logic
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authFetch from '../utils/authFetch';
import logger from '../utils/logger'; // Assuming a logger utility exists
import {
    Box,
    Typography,
    Button,
    Container,
    Paper,
    CircularProgress,
    Alert,
    Link,
    Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const OnboardingTraining = () => {
    const { user, refetchUserProfile } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isCompleted, setIsCompleted] = useState(user?.hasCompletedTraining || false);

    const handleCompleteTraining = async () => {
        setIsLoading(true);
        setError(null);
        logger.info(`OnboardingTraining: User ${user?.id} attempting to complete training.`);

        try {
            const response = await authFetch('/api/users/me/complete-training', {
                method: 'POST',
            });

            logger.debug("OnboardingTraining: Backend response:", response); // Use logger

            await refetchUserProfile();

            setIsCompleted(true);
            logger.info(`User ${user?.id} successfully marked training as complete.`);

            const from = location.state?.from?.pathname || '/cockpit';
            logger.info(`OnboardingTraining: Navigating to ${from}`);
            navigate(from, { replace: true });

        } catch (err) {
            logger.error("OnboardingTraining: Error completing training:", err); // Use logger
            setError(err.detail || err.message || 'An error occurred while marking training as complete. Please try again.');
            setIsLoading(false);
        }
    };

    // Optional: Redirect immediately if already completed when component mounts
    useEffect(() => {
        if (user?.hasCompletedTraining) {
            logger.info("User already completed training, redirecting...");
            const from = location.state?.from?.pathname || '/cockpit';
            navigate(from, { replace: true });
        }
    }, [user, navigate, location]);


    return (
        <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
                <Typography component="h1" variant="h4" align="center" gutterBottom>
                    MedsAi Onboarding Training
                </Typography>

                {isCompleted && !isLoading && (
                    <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
                        You have already completed the required onboarding training.
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                {/* --- Placeholder Training Content --- */}
                <Typography variant="h6" gutterBottom>
                    Welcome, {user?.firstName || 'User'}!
                </Typography>
                <Typography paragraph>
                    This mandatory training provides essential information about using the MedsAi platform
                    effectively and securely. Please review the following sections carefully.
                </Typography>

                <Box sx={{ my: 3 }}>
                    <Typography variant="h6" gutterBottom>Section 1: Platform Overview</Typography>
                    <Typography paragraph>
                        [Placeholder: Insert description of platform goals, key features, user roles, etc.]
                    </Typography>
                </Box>

                <Box sx={{ my: 3 }}>
                    <Typography variant="h6" gutterBottom>Section 2: Security & HIPAA Compliance</Typography>
                    <Typography paragraph>
                        [Placeholder: Detail HIPAA requirements, data security measures, user responsibilities for protecting PHI, password policies, session timeouts, etc.]
                    </Typography>
                    <Typography paragraph>
                        It is crucial to understand and adhere to all security protocols to ensure patient privacy and data integrity.
                    </Typography>
                    <Typography paragraph>
                        Refer to the full <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link> and <Link href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link> for details.
                    </Typography>
                </Box>

                <Box sx={{ my: 3 }}>
                    <Typography variant="h6" gutterBottom>Section 3: Key Workflows</Typography>
                    <Typography paragraph>
                        {/* Corrected Syntax */}
                        {`[Placeholder: Describe the <30s initiation flow, assigning content, monitoring patient data, logging time/events, generating reports, communication features, etc.]`}
                    </Typography>
                </Box>
                {/* --- End Placeholder Training Content --- */}

                <Divider sx={{ my: 2 }} />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}

                {!isCompleted && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleCompleteTraining}
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutlineIcon />}
                        >
                            {isLoading ? 'Processing...' : 'Acknowledge & Complete Training'}
                        </Button>
                    </Box>
                )}

                {isCompleted && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(location.state?.from?.pathname || '/cockpit', { replace: true })}
                        >
                            Proceed to Platform
                        </Button>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default OnboardingTraining;